import React, { useState, useEffect } from 'react';
import {
  Sparkles, TrendingUp, AlertTriangle, Briefcase, Stethoscope,
  Flame, Plane, FileX, DollarSign, Users, Clock, Activity,
} from 'lucide-react';
import { canView } from '../../lib/aiPermissions';

/* ──────────────────────────────────────────────────────────────────────────
 * AIIntelligencePanel — Phase F.2
 *
 * Drop-in panel for dashboards. Renders role-appropriate AI metrics:
 *   - sales       : intent score distribution, hot leads, ready-to-book pipeline
 *   - operations  : no-show risk distribution, travel/document readiness
 *   - marketing   : intent score by lead source, source volume
 *   - clinical    : pending reviews, recent generations by type
 *
 * Permission-gated: if the user's role doesn't have view access for the kind,
 * the panel returns null (silent gate — won't even show on the dashboard).
 *
 * Props:
 *   kind     — 'sales' | 'operations' | 'marketing' | 'clinical'
 *   role     — current user's role (used for client-side permission gate;
 *              backend enforces ground truth)
 * ────────────────────────────────────────────────────────────────────────── */

const KIND_META = {
  sales:       { label: 'Sales AI Intelligence',       icon: TrendingUp,  color: '#A855F7' },
  operations:  { label: 'Operations AI Intelligence',  icon: Briefcase,   color: '#F59E0B' },
  marketing:   { label: 'Marketing AI Intelligence',   icon: Activity,    color: '#0EA5E9' },
  clinical:    { label: 'Clinical AI Intelligence',    icon: Stethoscope, color: '#10B981' },
};

export default function AIIntelligencePanel({ kind, role: roleProp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedRole, setResolvedRole] = useState(roleProp || null);

  // Resolve role if not passed by parent
  useEffect(() => {
    if (roleProp) { setResolvedRole(roleProp); return; }
    let cancelled = false;
    (async () => {
      try {
        const me = await window.zignagenixAPI.getOwnProfile();
        if (!cancelled) setResolvedRole(me?.role || 'unknown');
      } catch {
        if (!cancelled) setResolvedRole('unknown');
      }
    })();
    return () => { cancelled = true; };
  }, [roleProp]);

  const featureId = `dashboard_${kind}_intelligence`;
  const allowed = resolvedRole && canView(resolvedRole, featureId);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await window.zignagenixAPI.getDashboardIntelligence(kind);
        if (cancelled) return;
        if (!r || r.error) {
          setError(r?.error || 'Failed to load intelligence');
        } else {
          setData(r);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, allowed]);

  // Silent role gate
  if (!allowed) return null;

  const meta = KIND_META[kind];
  const Icon = meta.icon;

  return (
    <div style={{
      background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12,
      padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Icon size={16} color={meta.color} />
        <h3 style={{ fontSize: 14, fontWeight: 700, color: meta.color, margin: 0 }}>
          {meta.label}
        </h3>
        <Sparkles size={11} color={meta.color} style={{ opacity: 0.5 }} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 9, color: '#94A3B8', textTransform: 'uppercase' }}>
          AI-derived · refresh on reload
        </span>
      </div>

      {loading && <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading intelligence…</div>}
      {error && <div style={{ fontSize: 12, color: '#DC2626' }}>✗ {error}</div>}

      {data && kind === 'sales' && <SalesPanel data={data} />}
      {data && kind === 'operations' && <OperationsPanel data={data} />}
      {data && kind === 'marketing' && <MarketingPanel data={data} />}
      {data && kind === 'clinical' && <ClinicalPanel data={data} />}
    </div>
  );
}

/* ─── Per-kind sub-panels ────────────────────────────────────────────────── */

function StatCard({ label, value, icon: Icon, color, subtitle }) {
  return (
    <div style={{
      flex: '1 1 160px', minWidth: 160,
      padding: 12, borderRadius: 10,
      background: color + '12', border: `1px solid ${color}33`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {Icon && <Icon size={12} color={color} />}
        <span style={{ fontSize: 9, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function MiniBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569', marginBottom: 2 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700, color: '#0F172A' }}>{count}</span>
      </div>
      <div style={{ height: 6, background: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function SalesPanel({ data }) {
  const totalScored = (data.intentDistribution || []).reduce((s, r) => s + (r.n || 0), 0);
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <StatCard label="Hot Leads" value={data.hotLeadCount || 0}
          icon={Flame} color="#DC2626"
          subtitle="manual hot OR intent ≥8" />
        <StatCard label="Ready to Book" value={data.readyToBookCount || 0}
          icon={DollarSign} color="#15803D"
          subtitle="high intent + financial ready" />
        <StatCard label="Stale High-Intent" value={data.staleHighIntentCount || 0}
          icon={Clock} color="#F59E0B"
          subtitle="intent ≥7, not contacted 7d+" />
        <StatCard label="Scored Contacts" value={totalScored}
          icon={Users} color="#A855F7"
          subtitle="have a Patient Intent score" />
      </div>
      {totalScored > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
            Intent score distribution
          </div>
          {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(score => {
            const row = (data.intentDistribution || []).find(r => r.score === score);
            if (!row || !row.n) return null;
            const color = score >= 8 ? '#15803D' : score >= 5 ? '#F59E0B' : '#94A3B8';
            return <MiniBar key={score} label={`Score ${score}`} count={row.n} total={totalScored} color={color} />;
          })}
        </div>
      )}
    </>
  );
}

function OperationsPanel({ data }) {
  const totalRisked = (data.noShowRiskDistribution || []).reduce((s, r) => s + (r.n || 0), 0);
  const high = (data.noShowRiskDistribution || []).find(r => r.risk === 'high')?.n || 0;
  const medium = (data.noShowRiskDistribution || []).find(r => r.risk === 'medium')?.n || 0;
  const low = (data.noShowRiskDistribution || []).find(r => r.risk === 'low')?.n || 0;

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <StatCard label="High No-Show Risk" value={high}
          icon={AlertTriangle} color="#DC2626"
          subtitle={`${totalRisked} contacts scored`} />
        <StatCard label="Travel Outstanding" value={data.travelOutstandingCount || 0}
          icon={Plane} color="#F59E0B"
          subtitle="hotel / transport / passport" />
        <StatCard label="Missing Docs (paid)" value={data.missingDocsCount || 0}
          icon={FileX} color="#DC2626"
          subtitle="paid but no docs received" />
      </div>
      {totalRisked > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
            No-show risk
          </div>
          <MiniBar label="High" count={high} total={totalRisked} color="#DC2626" />
          <MiniBar label="Medium" count={medium} total={totalRisked} color="#F59E0B" />
          <MiniBar label="Low" count={low} total={totalRisked} color="#10B981" />
        </div>
      )}
      {Array.isArray(data.paymentDistribution) && data.paymentDistribution.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
            Payment status
          </div>
          {data.paymentDistribution.map((row, i) => (
            <MiniBar key={i} label={(row.status || '').replace(/_/g, ' ')} count={row.n}
              total={data.paymentDistribution.reduce((s, r) => s + (r.n || 0), 0)}
              color="#0EA5E9" />
          ))}
        </div>
      )}
    </>
  );
}

function MarketingPanel({ data }) {
  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
          Avg Patient Intent score by source (≥3 scored contacts)
        </div>
        {(data.intentBySource || []).length === 0 ? (
          <div style={{ fontSize: 11, color: '#94A3B8' }}>Not enough scored contacts to compute by-source averages yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#475569', fontWeight: 600 }}>Source</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#475569', fontWeight: 600 }}>Avg intent</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#475569', fontWeight: 600 }}># scored</th>
              </tr>
            </thead>
            <tbody>
              {(data.intentBySource || []).map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '6px 8px', color: '#0F172A' }}>{row.lead_source || '(unknown)'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700,
                    color: parseFloat(row.avg_intent) >= 7 ? '#15803D' : parseFloat(row.avg_intent) >= 5 ? '#F59E0B' : '#94A3B8' }}>
                    {parseFloat(row.avg_intent).toFixed(2)}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B' }}>{row.scored_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function ClinicalPanel({ data }) {
  const pendingTotal = (data.pendingByType || []).reduce((s, r) => s + (r.n || 0), 0);
  const REQUEST_TYPE_LABEL = {
    protocol_match: 'Protocol Match',
    differential_dx: 'Differential Dx',
    lab_summary: 'Lab Summary',
    drug_interaction: 'Drug Interaction',
  };
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <StatCard label="Pending Reviews" value={pendingTotal}
          icon={Clock} color="#B45309"
          subtitle="awaiting physician decision" />
        <StatCard label="Reviewed (7d)" value={data.reviewedLast7Days || 0}
          icon={Activity} color="#15803D"
          subtitle="completed reviews this week" />
      </div>
      {pendingTotal > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
            Pending by type
          </div>
          {(data.pendingByType || []).map((row, i) => (
            <MiniBar key={i} label={REQUEST_TYPE_LABEL[row.request_type] || row.request_type}
              count={row.n} total={pendingTotal} color="#F59E0B" />
          ))}
        </div>
      )}
      {Array.isArray(data.topReviewersLast30Days) && data.topReviewersLast30Days.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
            Top reviewers (30 days)
          </div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#0F172A' }}>
            {data.topReviewersLast30Days.map((row, i) => (
              <li key={i}>{row.display_name} — <strong>{row.n}</strong> reviews</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
